/**
 * Admin Church Page: edit church info (title, welcome, schedule, address, contacts, useful chats).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import {
  IconMapPin,
  IconAlertTriangle,
  IconPlus,
  IconTrash,
  IconCheck,
  IconPhoto,
} from "../../../components/Icons.tsx";
import { getTelegram, haptic } from "../../../lib/telegram.ts";
import { useAuth } from "../../../lib/auth.tsx";
import {
  graphql,
  getErrorMessage,
  withMinDelay,
  getAuthHeaders,
  ADMIN_CHURCH_INFO_QUERY,
  UPDATE_CHURCH_INFO_MUTATION,
} from "../../../lib/graphql.ts";
import { config } from "../../../config.ts";
import { safeParseResponseJson } from "../../../lib/json-utils.ts";

interface UsefulChat {
  label: string;
  url: string;
}

interface ChurchInfoData {
  id: string;
  title: string | null;
  imageUrl: string | null;
  welcomeText: string | null;
  scheduleText: string | null;
  address: string | null;
  mapUrl: string | null;
  parkingText: string | null;
  phone: string | null;
  email: string | null;
  companyName: string | null;
  companyDetails: string | null;
  usefulChats: UsefulChat[];
  isActive: boolean;
}

const EMPTY_CHAT: UsefulChat = { label: "", url: "" };

export function AdminChurchPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { account } = useAuth();
  const hasLoadedOnce = useRef(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageCacheBust, setImageCacheBust] = useState(0);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [welcomeText, setWelcomeText] = useState("");
  const [scheduleText, setScheduleText] = useState("");
  const [address, setAddress] = useState("");
  const [mapUrl, setMapUrl] = useState("");
  const [parkingText, setParkingText] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyDetails, setCompanyDetails] = useState("");
  const [usefulChats, setUsefulChats] = useState<UsefulChat[]>([]);
  const [isActive, setIsActive] = useState(true);

  const loadData = useCallback(async () => {
    if (!account) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await withMinDelay(
        graphql<{ adminChurchInfo: ChurchInfoData | null }>(
          ADMIN_CHURCH_INFO_QUERY
        )
      );
      const raw = data.adminChurchInfo;
      if (raw) {
        setTitle(raw.title ?? "");
        setImageUrl(raw.imageUrl ?? "");
        setWelcomeText(raw.welcomeText ?? "");
        setScheduleText(raw.scheduleText ?? "");
        setAddress(raw.address ?? "");
        setMapUrl(raw.mapUrl ?? "");
        setParkingText(raw.parkingText ?? "");
        setPhone(raw.phone ?? "");
        setEmail(raw.email ?? "");
        setCompanyName(raw.companyName ?? "");
        setCompanyDetails(raw.companyDetails ?? "");
        setUsefulChats(
          raw.usefulChats?.length ? raw.usefulChats : [{ ...EMPTY_CHAT }]
        );
        setIsActive(raw.isActive ?? true);
      } else {
        setTitle("О церкви");
        setImageUrl("");
        setWelcomeText("");
        setScheduleText("");
        setAddress("");
        setMapUrl("");
        setParkingText("");
        setPhone("");
        setEmail("");
        setCompanyName("");
        setCompanyDetails("");
        setUsefulChats([{ ...EMPTY_CHAT }]);
        setIsActive(true);
      }
      hasLoadedOnce.current = true;
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [account]);

  useEffect(() => {
    if (account) loadData();
  }, [account, loadData]);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin/content");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  const handleSave = async () => {
    haptic("light");
    setIsSaving(true);
    setError(null);
    try {
      const chats = usefulChats.filter((c) => c.label.trim() || c.url.trim());
      await graphql<{ updateChurchInfo: ChurchInfoData }>(
        UPDATE_CHURCH_INFO_MUTATION,
        {
          input: {
            title: title.trim() || null,
            imageUrl: imageUrl.trim() || null,
            welcomeText: welcomeText.trim() || null,
            scheduleText: scheduleText.trim() || null,
            address: address.trim() || null,
            mapUrl: mapUrl.trim() || null,
            parkingText: parkingText.trim() || null,
            phone: phone.trim() || null,
            email: email.trim() || null,
            companyName: companyName.trim() || null,
            companyDetails: companyDetails.trim() || null,
            usefulChats: chats.length ? chats : null,
            isActive,
          },
        }
      );
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const addUsefulChat = () => {
    haptic("light");
    setUsefulChats((prev) => [...prev, { ...EMPTY_CHAT }]);
  };

  const removeUsefulChat = (index: number) => {
    haptic("light");
    setUsefulChats((prev) => prev.filter((_, i) => i !== index));
  };

  const updateUsefulChat = (
    index: number,
    field: "label" | "url",
    value: string
  ) => {
    setUsefulChats((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const handleImageUpload = async (file: File) => {
    setIsUploadingImage(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(
        `${config.apiUrl}/upload/content/church-page`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: formData,
        }
      );
      if (!response.ok) {
        const data = await safeParseResponseJson<{ error?: string }>(response, "church image upload");
        throw new Error(data.error || "Ошибка загрузки");
      }
      const result = await response.json() as { url: string };
      setImageUrl(result.url);
      setImageCacheBust(Date.now());
      haptic("success");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const fieldClass =
    "w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500";
  const labelClass =
    "block text-[12px] font-medium text-slate-400 uppercase tracking-wide mb-1";

  return (
    <Layout>
      <div className="pb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
            <IconMapPin className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900">О церкви</h1>
            <p className="text-[13px] text-slate-500">
              Текст, контакты, реквизиты
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 text-amber-600 text-sm">
            <IconAlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="card p-8 text-center text-slate-500 text-sm">
            Загрузка…
          </div>
        ) : (
          <div className="card overflow-hidden space-y-6 p-4">
            <div>
              <label className={labelClass}>Hero-картинка</label>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImageUpload(f);
                }}
              />
              {imageUrl && !isUploadingImage ? (
                <div className="relative rounded-xl overflow-hidden bg-slate-100 mb-2">
                  <img
                    src={
                      imageUrl +
                      (imageUrl.includes("?") ? "&" : "?") +
                      `_t=${imageCacheBust}`
                    }
                    alt=""
                    className="w-full aspect-[4/3] object-cover"
                  />
                </div>
              ) : null}
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={isUploadingImage}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 active:bg-slate-200 disabled:opacity-50"
                >
                  <IconPhoto className="w-4 h-4" />
                  {isUploadingImage
                    ? "Загрузка…"
                    : imageUrl
                    ? "Заменить"
                    : "Загрузить"}
                </button>
                {imageUrl && !isUploadingImage && (
                  <button
                    type="button"
                    onClick={() => setImageUrl("")}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 active:bg-amber-100"
                  >
                    Удалить
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className={labelClass}>Заголовок страницы</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="О церкви"
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Приветственный текст (HTML)</label>
              <textarea
                value={welcomeText}
                onChange={(e) => setWelcomeText(e.target.value)}
                rows={6}
                placeholder="Добро пожаловать..."
                className={`${fieldClass} resize-none`}
              />
            </div>
            <div>
              <label className={labelClass}>Расписание</label>
              <textarea
                value={scheduleText}
                onChange={(e) => setScheduleText(e.target.value)}
                rows={3}
                placeholder="Когда мы встречаемся..."
                className={`${fieldClass} resize-none`}
              />
            </div>
            <div>
              <label className={labelClass}>Адрес</label>
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={2}
                placeholder="Адрес"
                className={`${fieldClass} resize-none`}
              />
            </div>
            <div>
              <label className={labelClass}>Ссылка на карту</label>
              <input
                type="url"
                value={mapUrl}
                onChange={(e) => setMapUrl(e.target.value)}
                placeholder="https://..."
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Парковка (данные о парковке)</label>
              <textarea
                value={parkingText}
                onChange={(e) => setParkingText(e.target.value)}
                rows={2}
                placeholder="Рядом есть парковка..."
                className={`${fieldClass} resize-none`}
              />
            </div>
            <div>
              <label className={labelClass}>Телефон</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+66..."
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="info@..."
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Название организации</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="ООО «Церковь»"
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                Реквизиты и данные организации
              </label>
              <textarea
                value={companyDetails}
                onChange={(e) => setCompanyDetails(e.target.value)}
                rows={4}
                placeholder="Юр. адрес, ИНН..."
                className={`${fieldClass} resize-none`}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={labelClass}>Полезные чаты и каналы</label>
                <button
                  type="button"
                  onClick={addUsefulChat}
                  className="flex items-center gap-1 text-sm font-medium text-teal-600"
                >
                  <IconPlus className="w-4 h-4" />
                  Добавить
                </button>
              </div>
              <div className="space-y-3">
                {usefulChats.map((chat, index) => (
                  <div
                    key={index}
                    className="flex gap-2 items-start p-3 rounded-lg bg-slate-50"
                  >
                    <input
                      type="text"
                      value={chat.label}
                      onChange={(e) =>
                        updateUsefulChat(index, "label", e.target.value)
                      }
                      placeholder="Название"
                      className={`${fieldClass} flex-1 min-w-0`}
                    />
                    <input
                      type="url"
                      value={chat.url}
                      onChange={(e) =>
                        updateUsefulChat(index, "url", e.target.value)
                      }
                      placeholder="Ссылка"
                      className={`${fieldClass} flex-1 min-w-0`}
                    />
                    <button
                      type="button"
                      onClick={() => removeUsefulChat(index)}
                      className="flex-shrink-0 p-2 rounded-lg text-slate-500 hover:bg-slate-200 active:bg-slate-300"
                      aria-label="Удалить"
                    >
                      <IconTrash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="church-active"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
              />
              <label htmlFor="church-active" className="text-sm text-slate-700">
                Показывать страницу на сайте
              </label>
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {isSaving ? (
                "Сохранение…"
              ) : (
                <>
                  <IconCheck className="w-4 h-4" />
                  Сохранить
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
